import React, { CSSProperties, RefObject, useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { IPartyStageForUser, StreamType } from '../../PartyClientResponse';
import { VIDEO_ASPECT_RATIO, ZINDEX_TWITCH } from '../party/AppConstants';
import EnableAV from '../party/EnableAV';
import './MainVideoPlayer.scss';
import BackroomVideo from './BackroomVideo';
import { useAppState, ActionType } from '../../state/AppState';
import useResizeObserver from 'use-resize-observer';
import VolumeControls from '../party/VolumeControls';
import FullscreenParticipantVideoPlayer from '../../controller/video/FullscreenParticipantVideoPlayer';

// We should be able to pass this to Twitch
// parent={[
//   'http://dancefloor-app.firebaseapp.com',
//   'dancefloor-app.firebaseapp.com',
//   'fb-towers.firebaseapp.com',
//   'localhost:3000',
//   'localhost',
// ]}

// function useQuery() {
//   return new URLSearchParams(useLocation().search);
// }

const VidPlayer = ({
  setLocalTimestamp,
  url,
  start,
  width,
  height,
  volume,
  controls = false,
  fullscreen,
}: {
  controls?: boolean;
  setLocalTimestamp?: (val: number) => void;
  url: string;
  width: string | number;
  height: string | number;
  volume?: number;
  start?: string | number;
  fullscreen?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width: containerWidth = 0, height: containerHeight = 0 } =
    useResizeObserver<HTMLDivElement>({ ref });

  let props: { width?: string | number; height?: string | number } = {};
  let divStyle: CSSProperties = {};
  if (fullscreen) {
    divStyle = {
      position: 'fixed',
      height: '100%',
      width: '100%',
    };

    if (containerWidth / containerHeight < VIDEO_ASPECT_RATIO) {
      props.height = '100%';
      props.width = containerHeight * VIDEO_ASPECT_RATIO;
    } else {
      props.width = '100%';
      props.height = containerWidth / VIDEO_ASPECT_RATIO;
    }
  } else {
    props.height = '100%';
    props.width = '100%';
  }

  return (
    <div style={divStyle} ref={ref}>
      <ReactPlayer
        onProgress={(val) => {
          if (setLocalTimestamp) {
            setLocalTimestamp(val.playedSeconds);
          }
        }}
        className='react-player'
        playing
        controls={controls}
        config={{
          file: {
            forceHLS: true,
          },
          youtube: {
            playerVars: {
              start,
            },
          },
          twitch: {
            options: {
              muted: false,
            },
          },
        }}
        url={url}
        parent={[
          'dancefloor-app.web.com',
          'parent=dance.rptr.live',
          'fb-towers.firebaseapp.com',
          'localhost:3000',
          'localhost',
        ]}
        style={{
          zIndex: ZINDEX_TWITCH,
          transition: 'all 0.5s',
          background: 'transparent',
          position: 'absolute',
          bottom: 0,
        }}
        volume={volume}
        {...props}
      />
    </div>
  );
};

// const useVideoPlayer = ({width}) => {
//   const { party } = useAppState();
//   const stream = party?.stage?.stream;
//   let Player = () =>
//     <BackroomVideo
//       id={stage.stream.backroomStreamId}
//       transitionLength={stage.transitionLength}
//       volume={volume}
//       height={'100%'}
//     />;

//   if (!stream) {
//     return <div />
//   }

//   if (stream.type === StreamType.Twitch) {
//     url = 'https://www.twitch.tv/' + stage?.stream?.twitchId;
//   } else if (stage?.stream?.type === StreamType.Backroom) {
//   Player = () =>
//     <BackroomVideo
//       id={stage?.stream?.backroomStreamId!}
//       transitionLength={stage.transitionLength}
//       volume={volume}
//       height={'100%'}
//     />;
//     url = `https://www.youtube.com/watch?v=${stage?.stream?.youtubeId}`;
//   } else if (stage?.stream?.type === StreamType.Youtube) {
//     type === StreamType.Youtube ? true : false
//     url = ;
//     Player = (
//       <VidPlayer
//         controls={true}
//       url={`https://www.youtube.com/watch?v=${stage?.stream?.youtubeId}`}
//       width={width}
//       height={height}
//       />
//     )
//   } else {
//     url = 'https://www.twitch.tv/' + stage?.videoFeed.twitchId;
//   }
//   return {
//     Player
//   }
// }

const MainVideoPlayer = ({
  stage,
  volume,
  width,
  height,
  setLocalTimestamp,
}: {
  stage: IPartyStageForUser;
  volume: number;
  width: number;
  height: number | string;
  setLocalTimestamp?: (val: number) => void;
}) => {
  const { dispatch } = useAppState();
  const ref = useRef<HTMLDivElement>(null);
  const { width: containerWidth = 0, height: containerHeight = 0 } =
    useResizeObserver<HTMLDivElement>({ ref });

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    dispatch({
      type: ActionType.SetMainVideoPosition,
      position: {
        ref,
        left: ref.current.offsetLeft,
        top: ref.current.offsetTop,
        width: containerWidth,
        height: containerHeight,
      },
    });
  }, [ref, containerHeight, containerWidth, dispatch]);

  let player = <div />;

  if (stage?.stream?.type === StreamType.Twitch) {
    player = (
      <VidPlayer
        controls={true}
        url={'https://www.twitch.tv/' + stage?.stream?.twitchId}
        width={width}
        height={height}
        volume={volume}
        fullscreen
      />
    );
  } else if (stage?.stream?.type === StreamType.Backroom) {
    player = (
      <BackroomVideo
        id={stage?.stream?.backroomStreamId!}
        transitionLength={stage.transitionLength}
        volume={volume}
        height={height}
        width={width}
        audioSource={stage?.stream?.audioSource}
        fullscreen
      />
    );
    // url = `https://www.youtube.com/watch?v=${stage?.stream?.youtubeId}`;
  } else if (stage?.stream?.type === StreamType.Youtube) {
    player = (
      <VidPlayer
        controls={true}
        url={`https://www.youtube.com/watch?v=${stage?.stream?.youtubeId}`}
        width={width}
        height={height}
        volume={volume}
        fullscreen
      />
    );
  } else if (stage?.stream?.type === StreamType.Screenshare) {
    player = (
      <FullscreenParticipantVideoPlayer
        participantId={stage.stream.mainParticipantId}
        stage={stage}
        isScreenshare
      />
    );
  } else if (stage?.stream?.type === StreamType.Participant) {
    player = (
      <FullscreenParticipantVideoPlayer
        participantId={stage.stream.mainParticipantId}
        stage={stage}
        isScreenshare={false}
      />
    );
    // const left = ref.current?.offsetLeft;
    // const top = ref.current?.offsetTop;
    // console.log('width', containerWidth);
    // console.log('height', containerHeight);
    // const participant = stage.participants[stage?.stream?.mainParticipantId];
    // if (!participant || !party) {
    //   return <div />;
    // }
    // const radius = Math.min(containerWidth, containerHeight);
    // console.log(radius);
    // player = (
    //   <ParticipantBubble
    //     participant={participant}
    //     party={party}
    //     audio={'allowed'}
    //     video={'allowed'}
    //     top={0}
    //     left={0}
    //     radius={radius}
    //     square
    //   />
    // );
  } else {
    player = <div />;
    // url = 'https://www.twitch.tv/' + stage?.videoFeed.twitchId;
  }

  // let url = '';
  // if (stage?.stream?.type === StreamType.Twitch) {
  //   url = 'https://www.twitch.tv/' + stage?.stream?.twitchId;
  // } else if (stage?.stream?.type === StreamType.Youtube) {
  //   url = `https://www.youtube.com/watch?v=${stage?.stream?.youtubeId}`;
  // } else {
  //   url = 'https://www.twitch.tv/' + stage?.videoFeed.twitchId;
  // }

  // const timestampRef = useRef<number>(0);
  // useEffect(() => {
  //   if (timestampRef.current !== stage.videoFeed.playbackTimestamp) {

  //   }
  // }, [stage.videoFeed.playbackTimestamp])

  return (
    <div
      ref={ref}
      className={'main-video-player'}
      style={{
        zIndex: ZINDEX_TWITCH,
        // height,
      }}>
      {/* <DonateCTA /> */}
      <EnableAV />
      {player}
      {/* {stage?.stream?.type === StreamType.Backroom ? (
        <BackroomVideo
          id={stage.stream.backroomStreamId}
          transitionLength={stage.transitionLength}
          volume={volume}
          height={'100%'}
        />
      ) : (
        <ReactPlayer
          onProgress={(val) => {
            if (setLocalTimestamp) {
              setLocalTimestamp(val.playedSeconds);
            }
          }}
          className='react-player'
          playing
          controls={type === StreamType.Youtube ? true : false}
          config={{
            file: {
              forceHLS: true,
            },
            youtube: {
              playerVars: {
                start: stage.videoFeed.playbackTimestamp,
              },
            },
          }}
          url={url}
          parent={[
            'dancefloor-app.web.com',
            'parent=dance.rptr.live',
            'fb-towers.firebaseapp.com',
            'localhost:3000',
            'localhost',
          ]}
          style={{
            zIndex: ZINDEX_TWITCH,
            transition: 'all 0.5s',
            background: 'transparent',
          }}
          height={height}
          width={width}
          volume={volume}
        />
      )} */}
    </div>
  );
};

export default MainVideoPlayer;
